
import React, { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const isChunkLoadError = (error: Error | undefined): boolean => {
  if (!error || typeof error.message !== 'string') return false;
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module')
  );
};

const extractChunkUrl = (error: Error | undefined): string | null => {
  if (!error || typeof error.message !== 'string') return null;
  const m = error.message.match(/https?:\/\/[^\s'"]+\.m?js/);
  return m ? m[0] : null;
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Ignora erros de DOM não críticos (reconciliação React vs DOM real)
    const isDomReconciliationError =
      error.name === 'NotFoundError' &&
      error.message?.includes('not a child of this node') &&
      (error.message?.includes('removeChild') || error.message?.includes('insertBefore'));
    if (isDomReconciliationError) {
      console.warn('Erro de DOM não crítico ignorado:', error.message);
      return { hasError: false };
    }

    console.error('ErrorBoundary caught an error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Ignora erros de DOM não críticos (removeChild ou insertBefore)
    const isDomReconciliationError =
      error.name === 'NotFoundError' &&
      error.message?.includes('not a child of this node') &&
      (error.message?.includes('removeChild') || error.message?.includes('insertBefore'));
    if (isDomReconciliationError) {
      return;
    }

    console.error('Error details:', error, errorInfo);

    const tags: Record<string, string | boolean> = { boundary: 'app-error-boundary' };
    const extra: Record<string, unknown> = { componentStack: errorInfo?.componentStack };

    if (isChunkLoadError(error)) {
      tags.chunk_load_failure = true;
      const chunkUrl = extractChunkUrl(error);
      if (chunkUrl) tags.chunk_url = chunkUrl;

      // Conta quantas vezes a modal já apareceu nessa sessão
      let recoveryAttempt = 0;
      try {
        recoveryAttempt = Number(sessionStorage.getItem('error_modal_shown_count') || '0') + 1;
        sessionStorage.setItem('error_modal_shown_count', String(recoveryAttempt));
      } catch {
        // ignora
      }
      tags.recovery_attempt = String(recoveryAttempt);

      // Breadcrumb com snapshot do estado (sem bloquear o render)
      try {
        const swPromise = 'serviceWorker' in navigator
          ? navigator.serviceWorker.getRegistrations().then((r) => r.length).catch(() => -1)
          : Promise.resolve(0);
        const cachesPromise = 'caches' in window
          ? caches.keys().catch(() => [])
          : Promise.resolve([]);
        Promise.all([swPromise, cachesPromise]).then(([swCount, cacheNames]) => {
          Sentry.addBreadcrumb({
            category: 'chunk_load_failure',
            level: 'warning',
            message: 'ErrorBoundary chunk-load snapshot',
            data: {
              sw_registrations: swCount,
              cache_storage: cacheNames,
              href: window.location.href,
              recovery_attempt: recoveryAttempt,
            },
          });
        });
      } catch {
        // ignora
      }
    }

    Sentry.captureException(error, { tags, extra });
  }

  private clearRecoveryFlags = () => {
    try {
      sessionStorage.removeItem('module_reload_attempted');
      sessionStorage.removeItem('module_reload_timestamp');
      sessionStorage.removeItem('vite_preload_reload_at');
    } catch (e) {
      // ignorar
    }
  };

  private handleRetry = () => {
    this.clearRecoveryFlags();
    // Tira `_recovery` da URL pra dar nova chance ao recovery loop do lazyWithRetry
    const url = new URL(window.location.href);
    url.searchParams.delete('_recovery');
    url.searchParams.set('_cb', Date.now().toString());
    window.location.replace(url.toString());
  };

  private handleHardReload = async () => {
    // Nuke total: sessionStorage + localStorage + Caches API + SWs.
    // localStorage.clear() derruba auth da Supabase → cai em /auth ao redirecionar
    // pra `/`. Isso é o equivalente ao "Clear site data" do DevTools.
    try {
      sessionStorage.clear();
    } catch (e) {
      // ignorar
    }
    try {
      localStorage.clear();
    } catch (e) {
      // ignorar
    }
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((name) => caches.delete(name)));
      }
    } catch (e) {
      // ignorar
    }
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) {
      // ignorar
    }
    const url = new URL('/', window.location.origin);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.replace(url.toString());
  };

  private handleLogoutAndRestart = async () => {
    try {
      sessionStorage.clear();
    } catch (e) { /* ignorar */ }
    try {
      localStorage.clear();
    } catch (e) { /* ignorar */ }
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((name) => caches.delete(name)));
      }
    } catch (e) { /* ignorar */ }
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) { /* ignorar */ }
    const url = new URL('/auth', window.location.origin);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.replace(url.toString());
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Alert className="max-w-md">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Erro na Aplicação</AlertTitle>
            <AlertDescription className="mt-2">
              {this.state.error?.message || 'Ocorreu um erro inesperado.'}
            </AlertDescription>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={this.handleRetry}>
                Tentar Novamente
              </Button>
              <Button variant="outline" onClick={this.handleHardReload}>
                Limpar Cache e Recarregar
              </Button>
              <Button variant="destructive" onClick={this.handleLogoutAndRestart}>
                Sair e Recomeçar
              </Button>
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
